import React, { useState, useEffect, useRef } from 'react';
import './Timeline.css'; // Import the specific CSS for the timeline
import { techIconMap } from '../utils/techIcons'; // Import the centralized map

const Timeline = ({ items, title, position = 'left', id }) => {
  const [scrollProgress, setScrollProgress] = useState(0);
  const timelineRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      if (!timelineRef.current) return;

      const timelineRect = timelineRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;

      // Calculate progress based on the visible portion
      let progress = 0;
      if (timelineRect.height > 0) {
        // Consider progress only when the timeline is actually in view
        if (timelineRect.top < viewportHeight && timelineRect.bottom > 0) {
            // Calculate how much of the timeline (from top to bottom) has passed the middle of the viewport
            const scrollableViewportPoint = viewportHeight * 0.5; // Midpoint of the viewport
            const scrolledDistance = scrollableViewportPoint - timelineRect.top;
            progress = Math.max(0, Math.min(1, scrolledDistance / timelineRect.height));
        } else if (timelineRect.bottom <= 0) {
            // If the bottom of the timeline is above the viewport, it's fully scrolled past
            progress = 1;
        }
        // If the top is below the viewport, progress is 0 (handled by initialization)
      }

      setScrollProgress(progress);
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial calculation

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const timelineContainerClass = `timeline-container timeline-${position}`;

  return (
    <section id={id} className="timeline-section">
      <h2>{title}</h2>
      <div className={timelineContainerClass} ref={timelineRef}>
        <div className="timeline-line">
          <div
            className="timeline-progress"
            style={{ height: `${scrollProgress * 100}%` }}
          ></div>
        </div>
        <div className="timeline-items">
          {items.map((item, index) => (
            <div key={index} className="timeline-item">
              <div className="timeline-item-content">
                <h3>{item.title}</h3>
                {item.subtitle && <h4>{item.subtitle}</h4>}
                <p className="duration">
                  {item.duration}
                  {item.durationDetails && (
                    <span className="info-icon" title={item.durationDetails.join('\n')}>
                      ⓘ
                      <span className="tooltiptext">
                        {item.durationDetails.map((detail, i) => (
                          <span key={i} style={{ display: 'block' }}>{detail}</span>
                        ))}
                      </span>
                    </span>
                  )}
                </p>
                {item.details && (
                  <ul>
                    {item.details.map((detail, i) => (
                      <li key={i}>{detail}</li>
                    ))}
                  </ul>
                )}
              </div>
              {item.technologies && (
                <div className="timeline-item-tech-icons">
                  {item.technologies.map((tech, i) => (
                    <span key={i} title={tech} className="tech-icon">
                      {techIconMap[tech] || tech} {/* Use imported map */}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Timeline;
